import { useLocation, useParams } from 'react-router-dom';
import { Mail, Edit2 } from 'lucide-react';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface ParticipantInfo {
  firstName: string;
  lastName: string;
  email: string;
}

interface LocationState {
  mentor: ParticipantInfo;
  mentee: ParticipantInfo;
}

export function SessionDetails() {
  const { id } = useParams();
  const location = useLocation();
  const { mentor, mentee } = location.state as LocationState;
  const [mentorLinkId] = useState(() => uuidv4());
  const [menteeLinkId] = useState(() => uuidv4());
  const [isEditingMentor, setIsEditingMentor] = useState(false);
  const [isEditingMentee, setIsEditingMentee] = useState(false);
  const [mentorMessage, setMentorMessage] = useState(
    `Dear ${mentor.firstName} ${mentor.lastName},

You have been invited to be a mentor in the VINCER mentorship program. To set your availability and preferences for this mentorship session, please click the link below:`
  );
  const [menteeMessage, setMenteeMessage] = useState(
    `Dear ${mentee.firstName} ${mentee.lastName},

You have been matched with a mentor in the VINCER mentorship program. To select your preferred meeting time from your mentor's available slots, please click the link below:`
  );

  const baseUrl = window.location.origin;
  const mentorLink = `${baseUrl}/find-time/${id}/mentor/${mentorLinkId}/connect`;
  const menteeLink = `${baseUrl}/find-time/${id}/mentee/${menteeLinkId}`;

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold mb-8">Session Details</h1>
      <div className="grid gap-12">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-xl font-semibold mb-4">Session Information</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Mentor</h3>
              <p className="text-gray-900">{mentor.firstName} {mentor.lastName}</p>
              <p className="text-gray-600">{mentor.email}</p>
            </div>
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Mentee</h3>
              <p className="text-gray-900">{mentee.firstName} {mentee.lastName}</p>
              <p className="text-gray-600">{mentee.email}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-xl font-semibold mb-4">Mentor Invitation</h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            {isEditingMentor ? (
              <textarea
                value={mentorMessage}
                onChange={(e) => setMentorMessage(e.target.value)}
                className="w-full h-40 p-2 border rounded-lg focus:ring-2 focus:ring-[#ffcc32] focus:border-[#ffcc32] outline-none mb-4"
              />
            ) : (
              <div className="whitespace-pre-wrap text-gray-800 mb-4">
                {mentorMessage}
              </div>
            )}
            <a 
              href={mentorLink}
              className="text-blue-600 hover:text-blue-800 underline break-all"
            >
              {mentorLink}
            </a>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsEditingMentor(!isEditingMentor)}
              className="flex items-center gap-2 bg-gray-100 text-gray-700 font-medium px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Edit2 className="w-4 h-4" /> {isEditingMentor ? 'Save Changes' : 'Edit Message'}
            </button>
            <button
              className="flex items-center gap-2 bg-[#ffcc32] text-black font-medium px-4 py-2 rounded-lg hover:bg-[#ffd65c] transition-colors"
            >
              <Mail className="w-4 h-4" />
              Send Mentor Invitation
            </button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-xl font-semibold mb-4">Mentee Invitation</h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            {isEditingMentee ? (
              <textarea
                value={menteeMessage}
                onChange={(e) => setMenteeMessage(e.target.value)}
                className="w-full h-40 p-2 border rounded-lg focus:ring-2 focus:ring-[#ffcc32] focus:border-[#ffcc32] outline-none mb-4"
              />
            ) : (
              <div className="whitespace-pre-wrap text-gray-800 mb-4">
                {menteeMessage}
              </div>
            )}
            <a 
              href={menteeLink}
              className="text-blue-600 hover:text-blue-800 underline break-all"
            >
              {menteeLink}
            </a>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsEditingMentee(!isEditingMentee)}
              className="flex items-center gap-2 bg-gray-100 text-gray-700 font-medium px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Edit2 className="w-4 h-4" /> {isEditingMentee ? 'Save Changes' : 'Edit Message'}
            </button>
            <button
              className="flex items-center gap-2 bg-[#ffcc32] text-black font-medium px-4 py-2 rounded-lg hover:bg-[#ffd65c] transition-colors"
            >
              <Mail className="w-4 h-4" />
              Send Mentee Invitation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}